import { useState, useCallback, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DatePicker } from "@/components/ui/date-picker";
import { Search, X, ChevronDown, Funnel } from "lucide-react";
import { format, isValid } from "date-fns";
import { Config } from "@employee-management/common";
import clsx from "clsx";
import { useDebounceCallback } from "usehooks-ts";
import { onKeyDownNumberOnly } from "@/lib/number-input.util";

// Currency symbol for numeric ranges
const CURRENCY_SYMBOL = Config.i18n.currency;

export interface FilterOption {
  key: string;
  label: string;
  type: "text" | "select" | "boolean" | "range";
  rangeType?: "date" | "number";
  options?: { value: string; label: string }[];
  defaultValueLabel?: string;
  defaultValue?: string | RangeValue;
}

export interface RangeValue {
  min?: string;
  max?: string;
}

export interface FilterSettings {
  searchTerm: string;
  filters: Record<string, string | RangeValue>;
}

export interface SearchFilterBarProps {
  filterOptions?: FilterOption[];
  placeholder?: string;
  className?: string;
  initialValue?: FilterSettings;
  onFilterChange: (settings: FilterSettings) => void;
}

export function SearchFilterBar({
  filterOptions = [],
  placeholder = "Search...",
  className = "",
  initialValue,
  onFilterChange,
}: SearchFilterBarProps) {
  const [searchTerm, setSearchTerm] = useState(initialValue?.searchTerm || "");
  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | RangeValue>
  >(initialValue?.filters || {});
  const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});
  const [popoverSearchTerm, setPopoverSearchTerm] = useState("");
  const [localRangeValues, setLocalRangeValues] = useState<
    Record<string, RangeValue>
  >({});

  const userSelectedFilters = useMemo(() => {
    return new Set(
      Object.entries(activeFilters).filter(([key, value]) => {
        const option = filterOptions.find((opt) => opt.key === key);
        console.log(option, value, value !== option?.defaultValue);

        return value !== option?.defaultValue;
      })
    );
  }, [activeFilters, filterOptions]);

  // Debounced callback for search term changes
  const debouncedOnFilterChange = useDebounceCallback(
    useCallback(
      (settings: FilterSettings) => {
        onFilterChange(settings);
      },
      [onFilterChange]
    ),
    300
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedOnFilterChange({
      searchTerm: value,
      filters: activeFilters,
    });
  };

  const handleFilterChange = (key: string, value: string | RangeValue) => {
    const newFilters = {
      ...activeFilters,
      [key]: value,
    };
    setActiveFilters(newFilters);

    // Immediate call for filter changes (not debounced)
    onFilterChange({
      searchTerm,
      filters: newFilters,
    });
  };

  const clearFilter = (key: string) => {
    const option = filterOptions.find((opt) => opt.key === key);
    const defaultValue = option?.defaultValue;

    const newFilters = { ...activeFilters };
    if (defaultValue !== undefined) {
      newFilters[key] = defaultValue;
    } else {
      delete newFilters[key];
    }

    setActiveFilters(newFilters);
    setLocalRangeValues((prev) => {
      const newLocal = { ...prev };
      delete newLocal[key];
      return newLocal;
    });

    // Immediate call for filter changes (not debounced)
    onFilterChange({
      searchTerm,
      filters: newFilters,
    });
  };

  const clearAllFilters = () => {
    // Reset to default values or empty
    const newFilters: Record<string, string | RangeValue> = {};
    filterOptions.forEach((option) => {
      if (option.defaultValue !== undefined) {
        newFilters[option.key] = option.defaultValue;
      }
    });

    setActiveFilters(newFilters);
    setLocalRangeValues({});
    setSearchTerm("");

    // Immediate call for filter changes (not debounced)
    onFilterChange({
      searchTerm: "",
      filters: newFilters,
    });
  };

  const handleLocalRangeChange = (
    key: string,
    field: "min" | "max",
    value: string
  ) => {
    setLocalRangeValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleLocalDateRangeChange = (
    key: string,
    field: "min" | "max",
    date: Date | undefined
  ) => {
    setLocalRangeValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: date ? format(date, "yyyy-MM-dd") : undefined,
      },
    }));
  };

  const applyRangeFilter = (key: string) => {
    const localRange = localRangeValues[key];

    // Only apply if both values are present
    if (localRange?.min && localRange?.max) {
      handleFilterChange(key, localRange);
      setOpenPopovers((prev) => ({ ...prev, [key]: false }));
    }
  };

  const cancelRangeFilter = (key: string) => {
    // Reset local values to current active values
    const currentRange = activeFilters[key] as RangeValue;
    setLocalRangeValues((prev) => ({
      ...prev,
      [key]: currentRange || {},
    }));
    setOpenPopovers((prev) => ({ ...prev, [key]: false }));
  };

  const openRangePopover = (key: string) => {
    // Initialize local values with current active values
    const currentRange = activeFilters[key] as RangeValue;
    setLocalRangeValues((prev) => ({
      ...prev,
      [key]: currentRange || {},
    }));
    setOpenPopovers((prev) => ({ ...prev, [key]: true }));
  };

  const hasActiveFilters = searchTerm.trim() || userSelectedFilters.size > 0;

  const getRangeDisplayText = (option: FilterOption) => {
    const currentRange = (activeFilters[option.key] as RangeValue) || {};
    const isDefault =
      option.defaultValue !== undefined
        ? JSON.stringify(currentRange) === JSON.stringify(option.defaultValue)
        : !currentRange.min && !currentRange.max;

    if (isDefault) {
      return option.label;
    }

    if (option.rangeType === "date") {
      const labels = [];
      if (currentRange.min) {
        const minDate = new Date(currentRange.min);
        if (isValid(minDate)) {
          labels.push(format(minDate, "MMM dd"));
        }
      }
      if (currentRange.max) {
        const maxDate = new Date(currentRange.max);
        if (isValid(maxDate)) {
          labels.push(format(maxDate, "MMM dd"));
        }
      }
      return labels.length > 0
        ? option.label + ": " + labels.join(" - ")
        : option.label;
    } else {
      const labels = [];
      if (currentRange.min) {
        labels.push(`≥${currentRange.min}`);
      }
      if (currentRange.max) {
        labels.push(`≤${currentRange.max}`);
      }
      return labels.length > 0
        ? option.label + ": " + labels.join(" ")
        : option.label;
    }
  };

  const renderDateRangeInput = (option: FilterOption) => {
    const localRange = localRangeValues[option.key] || {};
    const minDate = localRange.min ? new Date(localRange.min) : undefined;
    const maxDate = localRange.max ? new Date(localRange.max) : undefined;

    return (
      <div className="space-y-3 p-2">
        <div className="text-sm font-medium">{option.label}</div>
        <div className="space-y-2">
          <DatePicker
            date={minDate}
            onDateChange={(date) =>
              handleLocalDateRangeChange(option.key, "min", date)
            }
            placeholder="From"
            className="h-8 w-full text-xs"
          />
          <DatePicker
            date={maxDate}
            onDateChange={(date) =>
              handleLocalDateRangeChange(option.key, "max", date)
            }
            placeholder="To"
            className="h-8 w-full text-xs"
          />
        </div>
        {(!localRange.min || !localRange.max) && (
          <div className="text-xs text-muted-foreground">
            Both start and end dates are required
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => cancelRangeFilter(option.key)}
            className="flex-1 h-8 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => applyRangeFilter(option.key)}
            disabled={!localRange.min || !localRange.max}
            className="flex-1 h-8 text-xs"
          >
            OK
          </Button>
        </div>
      </div>
    );
  };

  const renderNumberRangeInput = (option: FilterOption) => {
    const localRange = localRangeValues[option.key] || {};

    return (
      <div className="space-y-3 p-2">
        <div className="text-sm font-medium">{option.label}</div>
        <div className="space-y-2">
          <Input
            type="number"
            placeholder="Min"
            value={localRange.min || ""}
            onChange={(e) =>
              handleLocalRangeChange(option.key, "min", e.target.value)
            }
            className="h-8 text-xs"
            step="any"
            startContent={
              <span className="h-4 w-4 text-primary text-xs font-medium">
                {CURRENCY_SYMBOL}
              </span>
            }
            onKeyDown={(event) =>
              onKeyDownNumberOnly(event, () => applyRangeFilter(option.key))
            }
          />
          <Input
            type="number"
            placeholder="Max"
            value={localRange.max || ""}
            onChange={(e) =>
              handleLocalRangeChange(option.key, "max", e.target.value)
            }
            className="h-8 text-xs"
            step="any"
            startContent={
              <span className="h-4 w-4 text-primary text-xs font-medium">
                {CURRENCY_SYMBOL}
              </span>
            }
            onKeyDown={(event) =>
              onKeyDownNumberOnly(event, () => applyRangeFilter(option.key))
            }
          />
        </div>
        {(!localRange.min || !localRange.max) && (
          <div className="text-xs text-muted-foreground">
            Both minimum and maximum values are required
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => cancelRangeFilter(option.key)}
            className="flex-1 h-8 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => applyRangeFilter(option.key)}
            disabled={!localRange.min || !localRange.max}
            className="flex-1 h-8 text-xs"
          >
            OK
          </Button>
        </div>
      </div>
    );
  };

  const renderRangeInput = (option: FilterOption) => {
    if (option.rangeType === "date") {
      return renderDateRangeInput(option);
    }
    return renderNumberRangeInput(option);
  };

  const renderFilterInput = (option: FilterOption) => {
    if (option.type === "range") {
      const currValue = activeFilters[option.key] as RangeValue;
      const isAll =
        currValue?.min === undefined && currValue?.max === undefined;

      return (
        <Popover
          open={openPopovers[option.key]}
          onOpenChange={(open) => {
            if (open) {
              openRangePopover(option.key);
            } else {
              setOpenPopovers((prev) => ({ ...prev, [option.key]: false }));
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-8 justify-between text-xs hover:bg-neutral-900/5 dark:hover:bg-neutral-900/95"
            >
              <div className="flex items-center gap-1.5">
                <Funnel
                  className={clsx(
                    "!h-3 !w-3 transition-[fill] duration-300",
                    isAll && "text-foreground",
                    isAll ? "fill-transparent" : "fill-current"
                  )}
                />
                {getRangeDisplayText(option)}
              </div>
              <ChevronDown className="h-3 w-3 !text-foreground opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            {renderRangeInput(option)}
          </PopoverContent>
        </Popover>
      );
    }

    const currValue = (activeFilters[option.key] as string) || "all";
    const formattedValue =
      option.options?.find((opt) => opt.value === currValue)?.label ||
      currValue;
    const isDefault =
      option.defaultValue !== undefined
        ? currValue === option.defaultValue
        : currValue === "all";

    return (
      <div className="flex items-center gap-2">
        <Select
          value={currValue}
          onValueChange={(value) => handleFilterChange(option.key, value)}
          onOpenChange={() => {
            setPopoverSearchTerm("");
          }}
        >
          <SelectTrigger className="h-8 w-auto !text-xs hover:bg-neutral-900/5 dark:hover:bg-neutral-900/95">
            <SelectValue placeholder={option.label}>
              <div className="flex items-center gap-1.5">
                <Funnel
                  className={clsx(
                    "!h-3 !w-3 transition-[fill] duration-300",
                    isDefault && "text-foreground",
                    isDefault ? "fill-transparent" : "fill-current"
                  )}
                />
                {isDefault && currValue === "all" ? (
                  option.defaultValueLabel || option.label
                ) : (
                  <>
                    {option.label}:{" "}
                    <span className="underline font-medium">
                      {formattedValue}
                    </span>
                  </>
                )}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <div
              className="p-2"
              onKeyUp={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <Input
                placeholder="Search..."
                startContent={<Search className="h-4 w-4" />}
                endContent={
                  popoverSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPopoverSearchTerm("")}
                      className="pointer-events-auto hover:cursor-pointer !-mr-2"
                    >
                      Clear
                    </Button>
                  )
                }
                value={popoverSearchTerm}
                onChange={(e) => setPopoverSearchTerm(e.target.value)}
                onKeyUp={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            </div>
            <div className="border-b -mx-1" />
            <SelectItem value={"all"}>
              {option.defaultValueLabel || "All"}
            </SelectItem>
            <div className="border-b -mx-1" />
            {option.options
              ?.filter((opt) =>
                opt.label
                  .toLowerCase()
                  .includes(popoverSearchTerm.toLowerCase())
              )
              .map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const formatRangeLabel = (option: FilterOption, rangeValue: RangeValue) => {
    if (option.rangeType === "date") {
      const labels = [];
      if (rangeValue.min) {
        const minDate = new Date(rangeValue.min);
        if (isValid(minDate)) {
          labels.push(`From: ${format(minDate, "MMM dd, yyyy")}`);
        }
      }
      if (rangeValue.max) {
        const maxDate = new Date(rangeValue.max);
        if (isValid(maxDate)) {
          labels.push(`To: ${format(maxDate, "MMM dd, yyyy")}`);
        }
      }
      return labels.join(" - ");
    } else {
      const labels = [];
      if (rangeValue.min) {
        labels.push(`Min: ${rangeValue.min}`);
      }
      if (rangeValue.max) {
        labels.push(`Max: ${rangeValue.max}`);
      }
      return labels.join(" - ");
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10"
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSearchChange("")}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Filter Options */}
      {filterOptions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {filterOptions.map((option) => (
            <div key={option.key} className="flex items-center gap-2">
              {renderFilterInput(option)}
            </div>
          ))}
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>

          {searchTerm && (
            <Badge variant="secondary" className="gap-1.5">
              Search: "{searchTerm}"
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSearchChange("")}
                className="h-4 w-4 p-0 hover:bg-transparent"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {Object.entries(activeFilters).map(([key, value]) => {
            if (!value) return null;

            const option = filterOptions.find((opt) => opt.key === key);

            if (option?.type === "range") {
              const rangeValue = value as RangeValue;
              if (!rangeValue.min && !rangeValue.max) return null;

              const rangeLabel = formatRangeLabel(option, rangeValue);

              return (
                <Badge key={key} variant="secondary" className="gap-1">
                  {option.label}: {rangeLabel}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => clearFilter(key)}
                    className="h-4 w-4 p-0 hover:bg-transparent"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              );
            }

            const stringValue = value as string;
            // If the value is the default value or "all", don't show the filter
            if (
              (option?.defaultValue && stringValue === option?.defaultValue) ||
              (!option?.defaultValue && stringValue === "all")
            ) {
              return null;
            }

            const optionLabel =
              option?.options?.find((opt) => opt.value === stringValue)
                ?.label || stringValue;

            return (
              <Badge key={key} variant="secondary" className="gap-1">
                {option?.label}: {optionLabel}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearFilter(key)}
                  className="h-4 w-4 p-0 hover:bg-transparent"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-6 text-xs"
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
